import dayjs from "dayjs";
import { ReactElement, useEffect, useState } from "react";
import { useMatch, useSearchParams } from "react-router";
import { useMediaQuery } from "usehooks-ts";
import { useShallow } from "zustand/react/shallow";
import { BuyerOnly } from "../../../components";
import { Alert } from "../../../components/alert/Alert";
import { BusinessSwitcherDialog } from "../../../components/menu/dialogs/BusinessSwitcherDialog";
import {
  BasketContextActionEnum,
  SellarContextActionEnum,
  useBasketContext,
  useSellarContext,
  useSellarUser,
} from "../../../hooks";
import { useSellarCurrentBusiness } from "../../../hooks/useSellarCurrentBusiness.shared";
import { useSignupContext } from "../../../hooks/useSignupContext.shared";
import { useStockListContext } from "../useStockListContext";
import { CategoryPageLayout } from "./CategoryPageLayout";
import { useStorefrontFilterStore } from "../StorefrontFilterStore";
import { CategoryPageNavigationBar } from "./CategoryPageNavigationBar";
import { EditExistingOrderDialog } from "./Storefront/EditExistingOrderDialog/EditExistingOrderDialog";
import { useEditExistingOrderStore } from "./Storefront/EditExistingOrderDialog/EditExistingOrderStore";
import { StorefrontPostcodeDialogContainer } from "./Storefront/StorefrontDialogs/StorefrontPostcodeDialog/StorefrontPostcodeDialogContainer";
import { StorefrontFilters } from "./Storefront/StorefrontFilters/StorefrontFilter";
import { StorefrontProductTags } from "./Storefront/StorefrontProductTags/StorefrontProductTags";
import { StorefrontRouteProductDialog } from "./Storefront/table/Dialog/StorefrontRouteProductDialog";
import { SupplierAnnouncementBanner } from "./SupplierAnnouncementBanner";
import { StorefrontPromotionLayout } from "./Storefront/StorefrontPromotion/StorefrontPromotionLayout";
import { StorefrontSupplierAvailabilityBanner } from "./Storefront/StorefrontSupplierBanner/StorefrontSupplierAvailabilityBanner";
import { StorefrontConsolidatedBasketBanner } from "./Storefront/StorefrontConsolidatedBasketBanner/StorefrontConsolidatedBasketBanner";
import { CATEGORY_BUYER } from "../../../constants/routes";

export const CategoryPage = (): ReactElement => {
  const [{ selectedBusinessId }, dispatch] = useSellarContext();
  const { user } = useSellarUser();
  const {
    supplierQuery,
    unAuthorisedUserPostcode,
    setUnAuthorisedUserPostcode,
    supplierStorefront,
    openOrderList,
    checkoutSelectedFulfillmentType,
    setCheckoutSelectedFulfillmentType,
    isStorefrontLoading,
  } = useStockListContext();

  const { signupDialogActive } = useSignupContext();
  const [{ order }, dispatchBasket] = useBasketContext();
  const { business } = useSellarCurrentBusiness();
  const [searchParams] = useSearchParams();
  const [postcodeDialogActive, setPostcodeDialogActive] = useState(false);
  const categoryId = useMatch(CATEGORY_BUYER.pattern)?.params.categoryId;
  const isMobile = useMediaQuery("(max-width: 700px)");
  const storefrontLink =
    searchParams.get("storefrontLink") ||
    searchParams.get("priceTierLink") ||
    searchParams.get("orderFlowToken");
  const hasSupplierBanner =
    supplierQuery.data?.id &&
    (!business ||
      business.Retailer ||
      supplierQuery.data.id === business.Supplier?.id) &&
    supplierQuery.data.SupplierAnnouncement;

  const [, setHeight] = useState<number>();

  const supplierAvailableFromDate =
    supplierQuery.data?.StorefrontAvailability.startDate;
  const supplierAvailableEndDate =
    supplierQuery.data?.StorefrontAvailability.endDate;

  const showAvailabilityBanner =
    supplierQuery.data?.StorefrontAvailability.type === "scheduled" &&
    supplierAvailableFromDate &&
    supplierAvailableEndDate;

  const {
    isEditExistingOrderDialogVisible,
    hideEditExistingOrderDialog,
    resetHasShownEditOrderExistingDialog,
  } = useEditExistingOrderStore(
    useShallow((state) => ({
      isEditExistingOrderDialogVisible: state.isEditExistingOrderDialogVisible,
      hideEditExistingOrderDialog: state.hideEditExistingOrderDialog,
      resetHasShownEditOrderExistingDialog:
        state.resetHasShownEditOrderExistingDialog,
    })),
  );

  const initializeShowOutOfStockProducts = useStorefrontFilterStore(
    (state) => state.initializeShowOutOfStockProducts,
  );

  // on first page render, reset shown edit order existing dialog state
  useEffect(() => {
    resetHasShownEditOrderExistingDialog();
  }, [resetHasShownEditOrderExistingDialog]);

  useEffect(() => {
    if (supplierQuery.data) {
      initializeShowOutOfStockProducts(
        supplierQuery.data.showOutOfStockProducts,
      );
    }
  }, [supplierQuery.data, initializeShowOutOfStockProducts]);

  // COMMENTING OUT FOR NOW UNTIL WE HAVE A BETTER SOLUTION

  useEffect(() => {
    if (
      storefrontLink &&
      user?.BusinessUsers &&
      user.BusinessUsers.length > 1 &&
      !searchParams.get("categoryId") &&
      !signupDialogActive &&
      !selectedBusinessId
    ) {
      dispatch({
        type: SellarContextActionEnum.CHANGE_BUSINESS_SWITCHER_ACTIVE,
        businessSwitcherActive: true,
      });
    }
  }, [
    user,
    storefrontLink,
    searchParams,
    signupDialogActive,
    selectedBusinessId,
    dispatch,
  ]);

  const navBarHeightClass =
    business?.Supplier &&
    business.Supplier.id === supplierQuery.data?.id &&
    !storefrontLink
      ? "134px"
      : undefined;

  return (
    <>
      <>
        <CategoryPageNavigationBar
          setPostcodeDialogActive={setPostcodeDialogActive}
        />
        {supplierQuery.data?.SupplierOutOfOffice?.outOfOffice && (
          <BuyerOnly>
            <div className={"p-2"}>
              <Alert
                message={`${supplierQuery.data.Business.name} are Out of Office, but you can still place orders with them as normal. Delivery date estimations have been adjusted accordingly.`}
                title={`Out of Office until ${dayjs(supplierQuery.data.SupplierOutOfOffice.returnDate).format("DD/MM/YY")}: '${supplierQuery.data.SupplierOutOfOffice.message}'`}
                className={"w-full"}
                type={"information"}
              />
            </div>
          </BuyerOnly>
        )}
        {hasSupplierBanner && supplierQuery.data?.SupplierAnnouncement && (
          <SupplierAnnouncementBanner
            setHeight={setHeight}
            supplierAnnouncement={supplierQuery.data.SupplierAnnouncement}
          />
        )}
        {showAvailabilityBanner && supplierQuery.data && (
          <StorefrontSupplierAvailabilityBanner
            availableFromDate={supplierAvailableFromDate}
            availableEndDate={supplierAvailableEndDate}
            supplier={{
              name: supplierQuery.data.Business.name,
              logoUrl: supplierQuery.data.Business.logoUrl ?? "",
              id: supplierQuery.data.id,
            }}
          />
        )}
        {supplierQuery.data && (
          <StorefrontConsolidatedBasketBanner supplier={supplierQuery.data} />
        )}
        <CategoryPageLayout
          onEnterPostcode={
            !user ? () => setPostcodeDialogActive(true) : undefined
          }
          navBarHeightClass={navBarHeightClass}
          supplier={supplierQuery.data}
          supplierIsLoading={supplierQuery.isLoading}
          supplierStorefront={supplierStorefront}
          order={order}
          onFulfillmentTypeChange={(fulfillmentType) => {
            if (fulfillmentType) {
              setCheckoutSelectedFulfillmentType(fulfillmentType);

              dispatchBasket({
                type: BasketContextActionEnum.CHANGE_FULFILLMENT_TYPE,
                fulfillmentType,
              });
            }
          }}
          checkoutSelectedFulfillmentType={checkoutSelectedFulfillmentType}
        >
          {isMobile && !categoryId && user && supplierQuery.data?.id && (
            <>
              <div
                className={
                  "sticky z-10 col-span-3 max-h-min overflow-auto bg-gray-100"
                }
                style={{
                  top: navBarHeightClass ?? "calc(var(--nav-height))",
                }}
              >
                <StorefrontFilters supplierId={supplierQuery.data.id} />
              </div>
            </>
          )}
          <div className={"col-span-3 flex flex-col gap-4 xl:col-span-2"}>
            {!isMobile && supplierQuery.data?.id && (
              <div
                className={"sticky z-10 bg-gray-100 py-2 lg:px-2"}
                style={{
                  top: navBarHeightClass ?? "calc(var(--nav-height))",
                }}
              >
                <StorefrontFilters supplierId={supplierQuery.data.id} />
              </div>
            )}
            <StorefrontPromotionLayout
              priceTierId={supplierStorefront?.PriceTier?.id}
              supplierId={supplierQuery.data?.id}
              supplierLogoUrl={supplierQuery.data?.Business.logoUrl ?? ""}
            />
            <div className="px-2">
              <StorefrontProductTags />
            </div>
          </div>
        </CategoryPageLayout>
        <StorefrontRouteProductDialog />
      </>

      {supplierQuery.data && !isStorefrontLoading && (
        <>
          <StorefrontPostcodeDialogContainer
            setPostcodeDialogActive={setPostcodeDialogActive}
            postcodeDialogActive={postcodeDialogActive}
            setUnAuthorisedUserPostcode={setUnAuthorisedUserPostcode}
            unAuthorisedUserPostcode={unAuthorisedUserPostcode}
            supplierQuery={supplierQuery}
            title={"View our pricing and availability"}
            description={
              "Enter your postcode to see pricing, availability and delivery options to your venue"
            }
          />
        </>
      )}
      <EditExistingOrderDialog
        active={isEditExistingOrderDialogVisible}
        openOrderList={openOrderList}
        onConfirm={(data) => {
          if (data.type === "ORDER_SELECTED") {
            // replace basket with existing order to edit
            dispatchBasket({
              type: BasketContextActionEnum.CHANGE_ORDER,
              order: data.order,
            });
          }

          hideEditExistingOrderDialog();
        }}
        onDismiss={() => {
          hideEditExistingOrderDialog();
          resetHasShownEditOrderExistingDialog();
        }}
      />
      {business && storefrontLink && <BusinessSwitcherDialog />}
    </>
  );
};
